/**
 * The Morning Line. `docs/PHASE-6B.md` section 6.
 *
 * **This is the reason to open the tab tomorrow**, and until Stage 4 it was built, routed, tested
 * and unreachable. `game/src/net/api.ts` contained the string "refill" exactly ZERO times, while
 * `server/src/routes/money.ts` had both endpoints live, `server/src/do/player.ts` implemented all
 * three of GAME-DESIGN 2.3's earned sources with authored in-voice locked reasons, and
 * `tools/acceptance/phase5.mjs` called the endpoint over raw HTTP to keep its bot playing. The
 * harness could reach what the UI could not, which is exactly why the gap survived a whole phase.
 *
 * Energy regenerates 360 a day against a bar of 91 at level 1 and 180 at cap, so without refills
 * a player has to log in three or more times a day just to avoid wasting regeneration. That is
 * the classic energy-bar trap and the escape hatch was the thing that was locked.
 *
 * Three things carry the design and all three are visible on this component:
 *
 *   - **The rack renders EVERY slot always**, including the ones not earned today, showing the
 *     authored `lockedReason`. The read is the teaching: the three sources are only legible if
 *     you can see the two you did not qualify for.
 *   - **The claim is separate from the spend.** Turning up is always enough; the spend is the
 *     decision.
 *   - **The Card pays Heat relief and nothing else**, and it cannot be bought at any price
 *     because Heat is not for sale.
 */

import { useEffect } from 'react'
import { CARD_MAX } from '@ashport/shared/formulas'
import { Icon } from '../ui/Icon'
import { Btn, Chip, Col, Panel, PanelHead, Row } from '../ui/Kit'
import { useSession } from '../game/session'
import { sz } from '../ui/viewport'

export function MorningLineCard() {
  const m = useSession((s) => s.morning)
  const busy = useSession((s) => s.busy)
  const load = useSession((s) => s.loadMorningLine)
  const claim = useSession((s) => s.claimCard)
  const spend = useSession((s) => s.spendRefill)

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  return (
    <Panel tone="gold">
      <PanelHead
        title="The Morning Line"
        icon="clock"
        accent="var(--gold)"
        sub={m ? `Card ${m.card} of ${m.cardMax}` : undefined}
      />
      <div style={{ padding: '9px 11px 11px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {!m ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)' }}>Reading the line.</p>
        ) : (
          <>
            {/* The Card, as seven marks. A number a player can see filling is worth more than a
                number they have to remember. */}
            <Row gap={3}>
              {Array.from({ length: CARD_MAX }, (_, i) => (
                <div
                  key={i}
                  title={`Day ${i + 1}`}
                  style={{
                    flex: 1,
                    height: 6,
                    background: i < m.card ? 'var(--gold)' : 'rgba(255,255,255,.07)',
                    border: '1px solid var(--line-soft)',
                  }}
                />
              ))}
            </Row>

            {/*
              * THE MOST IMPORTANT STRING IN THE FEATURE, and it comes off the wire rather than
              * being typed here, so the sentence a player reads and the arithmetic the server
              * runs cannot drift apart. It is the specific inverse of the reset-to-day-1 streak
              * the design literature identifies as the predatory core of streak design.
              */}
            <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.45 }}>
              {m.rule}
            </p>

            <Row gap={7} style={{ marginTop: 8 }}>
              <Icon n="heat" s={13} c="var(--heat)" />
              <span style={{ fontSize: 11.5, color: 'var(--ink)' }}>
                A refill costs you {m.heatCost} Heat
              </span>
              {m.nextHeatCost < m.heatCost && (
                <Chip c="var(--gold)">{m.nextHeatCost} tomorrow</Chip>
              )}
            </Row>

            <Btn
              v="gold"
              size="sm"
              full
              style={{ marginTop: 9 }}
              disabled={busy || !m.claimable}
              onClick={() => void claim()}
            >
              {m.claimable ? 'Claim today' : 'Claimed today'}
            </Btn>

            {/* ---- the rack. Every slot, always. ---- */}
            <Col gap={5} style={{ marginTop: 10 }}>
              {m.offers.map((o) => {
                const banked = o.kind === 'energy' ? m.banked.energy : m.banked.nerve
                return (
                  <Row
                    key={o.kind}
                    gap={8}
                    style={{
                      padding: '6px 7px',
                      background: 'rgba(255,255,255,.02)',
                      border: '1px solid var(--line-soft)',
                      alignItems: 'flex-start',
                      opacity: banked > 0 || o.available > 0 ? 1 : 0.66,
                    }}
                  >
                    <Icon
                      n={o.kind === 'energy' ? 'energy' : 'nerve'}
                      s={15}
                      c={o.kind === 'energy' ? 'var(--energy)' : 'var(--nerve)'}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Row gap={6}>
                        <span className="t-head" style={{ fontSize: sz(10.5), color: 'var(--ink-hi)', letterSpacing: '.1em' }}>
                          {o.kind}
                        </span>
                        {banked > 0 && <Chip c="var(--gold)">{banked} in pocket</Chip>}
                      </Row>
                      {/*
                        * THE AUTHORED REFUSAL, printed rather than hidden. A locked slot that
                        * says why is the only thing that teaches a player the third source
                        * exists at all.
                        */}
                      {o.lockedReason !== null && (
                        <div style={{ fontSize: sz(10.5), color: 'var(--ink-dim)', marginTop: 3, lineHeight: 1.4 }}>
                          {o.lockedReason}
                        </div>
                      )}
                    </div>
                    <Btn
                      size="sm"
                      disabled={busy || o.available <= 0}
                      onClick={() => void spend(o.kind)}
                    >
                      Spend
                    </Btn>
                  </Row>
                )
              })}
            </Col>

            <p style={{ margin: '9px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.45 }}>
              A refill is refused while the bar is over a quarter full, and below that it always
              fills you to at least three quarters. There is no moment worth waiting for, so there
              is no reason to run yourself to nothing first.
            </p>
          </>
        )}
      </div>
    </Panel>
  )
}
